import { notification } from 'antd';
import axios from 'axios';
import { startTransition } from 'react';

import { ACTION_TYPES } from './constants.js';
import { isLiteReportsEnabled } from '../../../js/featureFlags.js';

const baseUrl = process.env.REACT_APP_API_URL?.replace('/api/v1', '/api/v2');

const config = {
  headers: { Authorization: '' },
};

// Rollout flag for the lite report responses (no per-bucket taskIds; drill-down
// resolved on demand). ON by default; see js/featureFlags.js for the
// localStorage / REACT_APP_DISABLE_DASHBOARD_LITE_REPORTS resolution order.
// Re-exported here so existing importers of this module keep working.
export { isLiteReportsEnabled };

// The batch's rare degraded path (shared-facet aggregation fails or times out,
// then every report in that scope group is recomputed individually) is bounded
// server-side but does not fit the fast path's budget, so lite mode gets a
// wider client timeout. Flag off ⇒ today's 25s, unchanged.
const BATCH_TIMEOUT_MS = 25000;
const BATCH_TIMEOUT_LITE_MS = 40000;

export const aggregateQuery = (
  intl,
  wId,
  group,
  query,
  { signal } = {},
) => {
  const fn = '[aggregateQuery]';
  return async (dispatch) => {
    const workspaceId = wId || localStorage.getItem('workspaceId');

    const reportOn = 'task';
    const query_ids = query;
    const groupBy = group;

    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(
        // Modal preview never supports drill-down, so taskIds have no consumer
        // here. Keep this endpoint lite independently of the dashboard rollout
        // flag; old callers that omit the parameter retain the legacy shape.
        `${baseUrl}/report/${workspaceId}/query?reportOn=${reportOn}${query_ids}&groupBy=${groupBy}&includeTaskIds=false`,
        { ...config, signal },
      );
      dispatch({
        type: ACTION_TYPES.AGGREGATE_QUERY_SUCCESS,
        payload: res?.data?.data,
      });

      return res?.data?.data;
    } catch (error) {
      // A newer modal selection superseded this request. Cancellation is the
      // normal debounce path, not a user-visible report failure.
      if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
        return undefined;
      }
      console.error(
        `${fn}: Failed to aggregate the query`,
        error?.response?.data || error.message,
      );
      notification.error({
        message: intl.formatMessage({
          id: 'REPORT_GET_FAILURE',
          defaultMessage:
            'Failed to aggregate the query. Please try again later.',
        }),
        placement: 'bottomRight',
      });
    }
  };
};

export const createReport = (intl, data, callBack, { silent = false } = {}) => {
  const fn = '[createReport]';
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(`${baseUrl}/report`, data, config);
      dispatch({
        type: ACTION_TYPES.CREATE_REPORT_SUCCESS,
        payload: res?.data?.data,
      });

      if (!silent) {
        notification.success({
          message: intl.formatMessage({
            id: 'REPORT_CREATE_SUCCESS',
            defaultMessage: 'Report was successfully created.',
          }),
          placement: 'bottomRight',
        });
      }

      callBack && callBack(res?.data?.data);
    } catch (error) {
      console.error(
        `${fn}: Failed to update the report`,
        error?.response?.data || error.message,
      );
      notification.error({
        message: intl.formatMessage({
          id: 'REPORT_CREATE_FAILURE',
          defaultMessage:
            'Failed to create the report. Please try again later.',
        }),
        placement: 'bottomRight',
      });
    }
  };
};

export const getReportData = (intl, reportId, callBack) => {
  const fn = '[getReportData]';
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      // No query string at all while the flag is off — see isLiteReportsEnabled.
      const liteQuery = isLiteReportsEnabled() ? '?includeTaskIds=false' : '';
      const res = await axios.get(
        `${baseUrl}/report/${reportId}${liteQuery}`,
        config,
      );
      await new Promise(resolve => setTimeout(resolve, 0));

        dispatch({
          type: ACTION_TYPES.GET_REPORT_SUCCESS,
          payload: res?.data?.data,
        });

      callBack && callBack(res?.data?.data);
      return res?.data?.data
    } catch (error) {
      console.error(
        `${fn}: Failed to get the report`,
        error?.response?.data || error.message,
      );
      // Re-throw so the single call site (ReportContainer's individual fallback)
      // can settle its own loading state on failure instead of being stuck in a
      // permanent skeleton. This is the only dispatch site for getReportData, so
      // propagating the failure here is safe.
      throw error;
    }
  };
};

export const getReportsBatch = (intl, reportIds) => {
  const fn = '[getReportsBatch]';
  return async (dispatch) => {
    if (!reportIds?.length) return;
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const lite = isLiteReportsEnabled();
      // Bounded timeout via a per-call config (do NOT mutate the shared `config`
      // singleton other actions reuse). If the batch hangs, the timeout rejects,
      // the catch below runs, and the caller's .finally still flips batchDone so
      // every widget can fall back to its individual fetch instead of staying a
      // permanent skeleton.
      const res = await axios.post(
        `${baseUrl}/report/batch`,
        // Flag off ⇒ the body is exactly what it was before this change.
        lite ? { reportIds, includeTaskIds: false } : { reportIds },
        {
          ...config,
          timeout: lite ? BATCH_TIMEOUT_LITE_MS : BATCH_TIMEOUT_MS,
        },
      );
      const batchData = res?.data?.data || {};
      dispatch({
        type: ACTION_TYPES.GET_REPORTS_BATCH_SUCCESS,
        payload: batchData,
      });
      return batchData;
    } catch (error) {
      console.error(`${fn}: Failed to batch-fetch reports`, error?.response?.data || error.message);
    }
  };
};

export const updateReport = (intl, reportId, data, callBack) => {
  const fn = '[updateReport]';
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.put(
        `${baseUrl}/report/${reportId}/update`,
        data,
        config,
      );
      dispatch({
        type: ACTION_TYPES.UPDATE_REPORT_SUCCESS,
        payload: res?.data?.data,
      });

      callBack && callBack(res?.data?.data);

      notification.success({
        message: intl.formatMessage({
          id: 'REPORT_UPDATE_SUCCESS',
          defaultMessage: 'Report was successfully updated.',
        }),
        placement: 'bottomRight',
      });
    } catch (error) {
      console.error(
        `${fn}: Failed to update the report`,
        error?.response?.data || error.message,
      );
      notification.error({
        message: intl.formatMessage({
          id: 'REPORT_UPDATED_FAILURE',
          defaultMessage:
            'Failed to update the report. Please try again later.',
        }),
        placement: 'bottomRight',
      });
    }
  };
};

export const deleteReport = (intl, reportId, dashboardId) => {
  const fn = '[deleteReport]';
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.delete(`${baseUrl}/report/${reportId}/delete`, config);
      dispatch({
        type: ACTION_TYPES.DELETE_REPORT_SUCCESS,
        payload: { reportId, dashboardId },
      });

      notification.success({
        message: intl.formatMessage({
          id: 'REPORT_DELETE_SUCCESS',
          defaultMessage: 'Report was successfully deleted.',
        }),
        placement: 'bottomRight',
      });
    } catch (error) {
      console.error(
        `${fn}: Failed to delete the report`,
        error?.response?.data || error.message,
      );
      notification.error({
        message: intl.formatMessage({
          id: 'REPORT_DELETE_FAILURE',
          defaultMessage:
            'Failed to delete the report. Please try again later.',
        }),
        placement: 'bottomRight',
      });
    }
  };
};

export const getTasksForReportRequest =
  (intl, reportId) => async (dispatch) => {
    const fn = '[getTasksForReportRequest]';
    dispatch({
      type: ACTION_TYPES.EXPORT_REPORT_REQUEST,
      payload: { reportId },
    });
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(
        `${baseUrl}/report/${reportId}/tasks`,
        config,
      );
      dispatch({
        type: ACTION_TYPES.EXPORT_REPORT_REQUEST_SUCCESS,
        payload: { reportId },
      });
      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: ACTION_TYPES.EXPORT_REPORT_REQUEST_FAILURE,
        payload: { reportId },
      });
      console.error(
        `${fn}: failed to fetch dashboard`,
        error?.response?.data || error.message,
      );
      notification.error({
        message: intl.formatMessage({
          id: 'TASKS_FOR_REPORT_FETCH_FAILURE',
          defaultMessage:
            'Failed to fetch tasks for the report. Please try again later.',
        }),
        placement: 'bottomRight',
      });
      return {};
    }
  };

/**
 * Resolves the tasks behind ONE chart element without the report having shipped
 * their ids: the server re-derives them from the report's own query.
 *
 * @param {Object} descriptor — { group?, stack?, date? }; a burn bucket by
 *        `date`, an aggregated row by its RAW `group` (+ `stack` for one
 *        segment of a stacked/grouped bar), or — with every field absent — the
 *        same de-duplicated, novalue-excluded "see all" union the client used
 *        to compute from the embedded ids.
 *
 * Response shape and dispatched actions are deliberately identical to
 * getTaskPreviewsByIds, so every consumer treats the two interchangeably.
 */
export const getChartTasksDrilldown =
  (intl, reportId, descriptor, { offset = 0, limit = 100 } = {}) => async (dispatch) => {
    const fn = '[getChartTasksDrilldown]';
    dispatch({
      type: ACTION_TYPES.PREVIEW_TASKS_REQUEST,
      payload: { reportId, drilldown: descriptor },
    });
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      // Only the constrained dimensions are sent: an omitted field means "no
      // constraint" server-side, which is exactly how "see all" is expressed.
      const body = {};
      if (descriptor?.group !== undefined) body.group = descriptor.group;
      if (descriptor?.stack !== undefined) body.stack = descriptor.stack;
      if (descriptor?.date !== undefined) body.date = descriptor.date;
      body.offset = offset;
      body.limit = limit;

      const res = await axios.post(
        `${baseUrl}/report/${reportId}/tasks/drilldown`,
        body,
        config,
      );
      const responseData = res?.data?.data;
      const page = Array.isArray(responseData)
        ? {
            tasks: responseData,
            total: responseData.length,
            offset: 0,
            limit: responseData.length,
            hasMore: false,
          }
        : responseData;
      dispatch({
        type: ACTION_TYPES.PREVIEW_TASKS_REQUEST_SUCCESS,
        payload: { reportId, tasks: page?.tasks || [] },
      });
      return page;
    } catch (error) {
      dispatch({
        type: ACTION_TYPES.PREVIEW_TASKS_REQUEST_FAILURE,
        payload: { reportId },
      });
      console.error(
        `${fn}: failed to fetch chart tasks`,
        error?.response?.data || error.message,
      );
      notification.error({
        message: intl.formatMessage({
          id: 'TASKS_PREVIEW_FETCH_FAILURE',
          defaultMessage:
            'Failed to fetch tasks for preview. Please try again later.',
        }),
        placement: 'bottomRight',
      });
      return null;
    }
  };

export const getTaskPreviewsByIds =
  (intl, reportId, taskIds) => async (dispatch) => {
    const fn = '[getTaskPreviewsByIds]';
    dispatch({
      type: ACTION_TYPES.PREVIEW_TASKS_REQUEST,
      payload: { reportId, taskIds },
    });
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(
        `${baseUrl}/report/${reportId}/tasks/preview`,
        { taskIds },
        config,
      );
      dispatch({
        type: ACTION_TYPES.PREVIEW_TASKS_REQUEST_SUCCESS,
        payload: { reportId, tasks: res?.data?.data },
      });
      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: ACTION_TYPES.PREVIEW_TASKS_REQUEST_FAILURE,
        payload: { reportId },
      });
      console.error(
        `${fn}: failed to fetch dashboard`,
        error?.response?.data || error.message,
      );
      notification.error({
        message: intl.formatMessage({
          id: 'TASKS_PREVIEW_FETCH_FAILURE',
          defaultMessage:
            'Failed to fetch tasks for preview. Please try again later.',
        }),
        placement: 'bottomRight',
      });
      return {};
    }
  };
